import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Link from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import DevelopmentCard from "../components/DevelopmentCard";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api } from "../lib/api";

const CATEGORIES = [
  { key: "all", label: "dev_filter_all" },
  { key: "apartments", label: "nav_dev_apartments" },
  { key: "penthouses", label: "nav_dev_penthouses" },
  { key: "villas", label: "nav_dev_villas" },
  { key: "frontline-golf", label: "nav_dev_frontline" },
  { key: "sea-view", label: "nav_dev_seaview" },
];

export default function Developments() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [items, setItems] = useState([]);
  const { t } = useI18n();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/developments");
        setItems(data);
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (category === "all") return items;
    return items.filter((d) => (d.categories || []).includes(category));
  }, [items, category]);

  const activeLabel = t(CATEGORIES.find((c) => c.key === category)?.label || "dev_filter_all");

  useDocumentMeta({
    title: t("dev_list_meta_title", { category: activeLabel }),
    description: t("dev_list_meta_desc", { category: activeLabel.toLowerCase(), count: filtered.length }),
  });

  const handleCategory = (k) => {
    if (k === "all") setParams({});
    else setParams({ category: k });
  };

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup
        schema={buildBreadcrumb([
          { name: t("home"), url: "/" },
          { name: t("developments"), url: "/developments" },
          ...(category !== "all"
            ? [{ name: activeLabel, url: `/developments?category=${category}` }]
            : []),
        ])}
      />

      <section className="pt-40 pb-16 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">
            — {t("dev_list_eyebrow", { shown: filtered.length, total: items.length })}
          </p>
          <h1
            data-testid="developments-title"
            className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {category === "all" ? (
              <>
                {t("dev_list_heading")}
                <br />
                <em className="not-italic text-[#C5A880]">{t("dev_list_heading_em")}</em>
              </>
            ) : (
              <>
                {activeLabel}{" "}
                <em className="not-italic text-[#C5A880]">{t("dev_list_cat_heading_em")}</em>
              </>
            )}
          </h1>

          <div
            data-testid="category-filter"
            className="flex flex-wrap gap-2 mt-12 border-t border-[#E5DFD5] pt-8"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => handleCategory(c.key)}
                data-testid={`filter-${c.key}`}
                className={`px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
                  category === c.key
                    ? "bg-[#1A1A1A] text-[#F9F6F0]"
                    : "border border-[#E5DFD5] text-[#1A1A1A] hover:border-[#1A1A1A]"
                }`}
              >
                {t(c.label)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          {filtered.length === 0 ? (
            <p data-testid="no-results" className="text-lg text-[#73706B] py-16 text-center">
              {t("dev_list_no_results")}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
              {filtered.map((d, i) => (
                <DevelopmentCard key={d.slug} d={d} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-[#1A1A1A] text-[#F9F6F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="text-3xl lg:text-4xl font-light max-w-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("dev_list_cta_heading")}
          </h2>
          <Link to="/contact?request=price-list" data-testid="developments-cta-price" className="bg-[#C5A880] text-[#1A1A1A] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#F9F6F0] transition-colors">
            {t("request_price_list")}
          </Link>
        </div>
      </section>
    </div>
  );
}
